import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthState, Credentials } from "./types"; // Import AuthState and Credentials
import { User } from 'autocount-shared'; // Import User type from shared package
import { API_BASE_URL } from '../../config'; // Import from centralized config

// Define API URL from central config
const API_URL = API_BASE_URL;

// --- Interfaces specific to this context/API calls ---

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

export interface RegisterFormData {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

// --- Auth Context Shape ---

interface AuthContextType {
  authState: AuthState;
  login: (credentials: Credentials) => Promise<void>;
  register: (userData: RegisterFormData) => Promise<void>;
  logout: () => void;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  clearError: () => void;
  // Add missing properties that are used in components
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// Create context with a default undefined value initially
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// --- Auth Provider Component ---

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // State managed by useState, initialized from localStorage
  const [authState, setAuthState] = useState<AuthState>(() => {
    const token = localStorage.getItem('token');
    console.log("Initial AuthProvider state, token exists:", !!token);
    // Basic initialization - useEffect will fetch user details
    return {
      isAuthenticated: !!token,
      user: null,
      token: token || null,
      loading: !!token, // Start loading if token exists
      error: null,
      // Removed refreshToken from state for simplicity, manage via localStorage/API calls
      // Removed pendingRegistration, handle via UI feedback if needed
    };
  });

  // Effect to verify token and fetch user data on initial load or token change
  useEffect(() => {
    const verifyTokenAndFetchUser = async () => {
      const token = localStorage.getItem('token');
      console.log("Initial token verification, token exists:", !!token);
      
      if (!token) {
        console.log("No token found, setting not authenticated");
        setAuthState(prevState => ({ ...prevState, loading: false, isAuthenticated: false, user: null }));
        return;
      }

      // Start loading only if not already loading
      console.log("Token found, verifying with backend");
      setAuthState(prevState => ({ ...prevState, loading: true }));

      try {
        const response = await fetch(`${API_URL}/api/v1/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        console.log("Token verification response status:", response.status);
        if (response.ok) {
          const userData: User = await response.json();
          console.log("Token valid, user data retrieved:", userData.username);
          
          // Explicitly type the object literal
          const newState: AuthState = {
            isAuthenticated: true,
            user: userData,
            token: token,
            loading: false,
            error: null,
          };
          
          console.log("Setting authenticated state:", newState);
          setAuthState(newState);
        } else {
          // Token is invalid or expired
          console.error('Token verification failed:', response.status);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken'); // Also clear refresh token
          setAuthState(prevState => ({
            ...prevState,
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false,
            error: 'Session invalid or expired. Please log in again.',
          }));
        }
      } catch (error) {
        console.error('Auth initialization fetch error:', error);
        // Keep potentially stale token but mark as not authenticated until resolved
        setAuthState(prevState => ({
          ...prevState,
          isAuthenticated: false, // Mark as not authenticated on error
          user: null,
          loading: false,
          error: 'Failed to verify session. Check connection.',
        }));
      }
    };

    verifyTokenAndFetchUser();
  }, []); // Run only on mount

  // --- Context Actions ---

  const login = async (credentials: Credentials) => {
    console.log("Login attempt with credentials:", credentials.username);
    setAuthState(prevState => ({ ...prevState, loading: true, error: null }));
    try {
      console.log("Sending login request to:", `${API_URL}/api/v1/auth/login`);
      const response = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const data: LoginResponse | { message: string } = await response.json();
      console.log("Login response status:", response.status, "Response has token:", 'accessToken' in data);

      if (response.ok && 'accessToken' in data) {
        console.log("Login successful, storing tokens and setting auth state");
        
        // First store tokens in localStorage
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        
        // Then update auth state with a single setState call
        const newAuthState: AuthState = {
          isAuthenticated: true,
          user: data.user,
          token: data.accessToken,
          loading: false,
          error: null,
        };
        
        console.log("Setting new auth state:", newAuthState);
        setAuthState(newAuthState);
        console.log("Auth state updated after login:", newAuthState);
      } else {
        const errorMessage = (data as { message: string }).message || 'Login failed.';
        console.error('Login API Error:', errorMessage);
        setAuthState(prevState => ({
          ...prevState,
          loading: false,
          error: errorMessage,
        }));
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Login fetch error:', error);
      setAuthState(prevState => ({
        ...prevState,
        loading: false,
        error: 'Network error during login. Please try again.',
      }));
      throw error;
    }
  };

  const register = async (userData: RegisterFormData) => {
    setAuthState(prevState => ({ ...prevState, loading: true, error: null }));
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok) {
        // Indicate success, maybe prompt user to check email or wait for approval
        console.log('Registration successful (pending approval likely)');
        setAuthState(prevState => ({
          ...prevState,
          loading: false,
          error: null,
          // Potentially set a flag like 'registrationSubmitted: true'
        }));
        // You might want to return a success status or message here
      } else {
        const errorMessage = data.message || 'Registration failed.';
        console.error('Register API Error:', errorMessage);
        setAuthState(prevState => ({
          ...prevState,
          loading: false,
          error: errorMessage,
        }));
        // Re-throw or return error status if needed by calling component
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Register fetch error:', error);
      const errorMessage = 'Network error during registration.';
      setAuthState(prevState => ({
        ...prevState,
        loading: false,
        error: errorMessage,
      }));
      throw new Error(errorMessage); // Propagate error
    }
  };

  const logout = () => {
    const token = authState.token || localStorage.getItem('token'); // Use state token first

    // Clear local state immediately for faster UI update
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
      error: null,
    });

    // Clear storage
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');

    // Attempt to call the backend logout endpoint (fire and forget)
    if (token) {
      fetch(`${API_URL}/api/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(err => console.error('Logout API call failed:', err)); // Log error but don't block logout
    }
  };

  const updateProfile = async (userData: Partial<User>) => {
    setAuthState(prevState => ({ ...prevState, loading: true, error: null }));
    const token = authState.token;
    if (!token) {
      setAuthState(prevState => ({ ...prevState, error: "Not authenticated", loading: false }));
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/profile`, { // Assuming this is the endpoint
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (response.ok) {
        setAuthState(prevState => ({
          ...prevState,
          user: { ...prevState.user, ...data }, // Update user data
          loading: false,
          error: null,
        }));
      } else {
        const errorMessage = data.message || 'Failed to update profile.';
        setAuthState(prevState => ({ ...prevState, loading: false, error: errorMessage }));
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Update profile fetch error:', error);
      const errorMessage = 'Network error updating profile.';
      setAuthState(prevState => ({ ...prevState, loading: false, error: errorMessage }));
      throw new Error(errorMessage);
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    setAuthState(prevState => ({ ...prevState, loading: true, error: null }));
    const token = authState.token;
     if (!token) {
      setAuthState(prevState => ({ ...prevState, error: "Not authenticated", loading: false }));
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/password`, { // Assuming endpoint
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const data = await response.json(); // Assume backend sends message on success/failure

      if (response.ok) {
        console.log('Password updated successfully');
        setAuthState(prevState => ({ ...prevState, loading: false, error: null }));
        // Optionally display success message to user via state/callback
      } else {
        const errorMessage = data.message || 'Failed to update password.';
        setAuthState(prevState => ({ ...prevState, loading: false, error: errorMessage }));
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Update password fetch error:', error);
      const errorMessage = 'Network error updating password.';
      setAuthState(prevState => ({ ...prevState, loading: false, error: errorMessage }));
      throw new Error(errorMessage);
    }
  };

  const clearError = () => {
    setAuthState(prevState => ({ ...prevState, error: null }));
  };

  // --- Provide Context Value ---

  return (
    <AuthContext.Provider value={{
      authState,
      login,
      register,
      logout,
      updateProfile,
      updatePassword,
      clearError,
      user: authState.user,
      isAuthenticated: authState.isAuthenticated,
      isLoading: authState.loading,
      error: authState.error,
      changePassword: updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// --- Custom Hook for Using Auth Context ---

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 